import React, { useState, useEffect } from 'react';
import { dashboardAPI, toolsAPI, transactionsAPI } from '../services/api';

const statusClasses = {
  available: 'bg-emerald-100 text-emerald-700',
  checked_out: 'bg-blue-100 text-blue-700',
  overdue: 'bg-red-100 text-red-700',
  maintenance: 'bg-amber-100 text-amber-700',
  calibration: 'bg-orange-100 text-orange-700',
  retired: 'bg-slate-100 text-slate-600',
  active: 'bg-blue-100 text-blue-700',
  returned: 'bg-emerald-100 text-emerald-700',
  due: 'bg-amber-100 text-amber-700',
  scheduled: 'bg-indigo-100 text-indigo-700',
  excellent: 'bg-emerald-100 text-emerald-700',
  good: 'bg-green-100 text-green-700',
  fair: 'bg-yellow-100 text-yellow-700',
  poor: 'bg-red-100 text-red-700',
};

const Badge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusClasses[status] || 'bg-slate-100 text-slate-600'}`}>
    {status}
  </span>
);

const Th = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 text-sm text-slate-700 ${className}`}>{children}</td>
);

const statCards = [
  { key: 'available',   label: 'Available Tools',  field: 'available_tools',   iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', valueColor: 'text-emerald-700', border: 'border-l-4 border-l-emerald-500', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'checked_out', label: 'Checked Out',       field: 'checked_out_tools', iconBg: 'bg-blue-100',    iconColor: 'text-blue-600',    valueColor: 'text-blue-700',    border: 'border-l-4 border-l-blue-500',    icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
  { key: 'overdue',     label: 'Overdue Returns',   field: 'overdue_tools',     iconBg: 'bg-red-100',     iconColor: 'text-red-600',     valueColor: 'text-red-700',     border: 'border-l-4 border-l-red-500',     icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  { key: 'calibration', label: 'Calibration Due',   field: 'calibration_due',   iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   valueColor: 'text-amber-700',   border: 'border-l-4 border-l-amber-500',   icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  { key: 'low_stock',   label: 'Low Stock Items',   field: 'low_stock_items',   iconBg: 'bg-purple-100',  iconColor: 'text-purple-600',  valueColor: 'text-purple-700',  border: 'border-l-4 border-l-purple-500',  icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
];

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [checkedOutTools, setCheckedOutTools] = useState([]);
  const [calibrationDue, setCalibrationDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, checkedOutRes, calibrationRes] = await Promise.all([
        dashboardAPI.getSummary(),
        dashboardAPI.getCheckedOutTools(),
        dashboardAPI.getCalibrationDue(),
      ]);
      setSummary(summaryRes.data.summary);
      setCheckedOutTools(checkedOutRes.data.checkedOutTools || []);
      setCalibrationDue(calibrationRes.data.calibrationDue || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatCardClick = async (type) => {
    setModalType(type);
    setShowModal(true);
    setModalLoading(true);
    setModalData([]);
    setModalError(null);
    try {
      let data = [];
      switch (type) {
        case 'available': {
          const res = await toolsAPI.getAll();
          data = (res.data.tools || []).filter(t => t.status === 'available');
          break;
        }
        case 'checked_out': {
          const res = await dashboardAPI.getCheckedOutTools();
          data = res.data.checkedOutTools || [];
          break;
        }
        case 'overdue': {
          const res = await transactionsAPI.getAll();
          const today = new Date(); today.setHours(0, 0, 0, 0);
          data = (res.data.transactions || []).filter(tx => {
            if (tx.status !== 'active') return false;
            const due = new Date(tx.expected_return_date); due.setHours(0, 0, 0, 0);
            return due < today;
          }).map(tx => {
            const due = new Date(tx.expected_return_date);
            return { ...tx, days_overdue: Math.ceil((today - due) / (1000 * 60 * 60 * 24)) };
          });
          break;
        }
        case 'calibration': {
          const res = await dashboardAPI.getCalibrationDue();
          data = res.data.calibrationDue || [];
          break;
        }
        case 'low_stock': {
          const res = await dashboardAPI.getLowStock();
          data = res.data.lowStockItems || [];
          break;
        }
        default: data = [];
      }
      setModalData(data);
    } catch (error) {
      setModalError(`Failed to load data: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => { setShowModal(false); setModalType(''); setModalData([]); setModalError(null); };

  const getModalTitle = () => {
    const titles = {
      available: 'Available Tools',
      checked_out: 'Checked Out Tools',
      overdue: 'Overdue Returns',
      calibration: 'Calibration Due',
      low_stock: 'Low Stock Items',
    };
    return `${titles[modalType] || 'Details'} (${modalData.length})`;
  };

  const renderModalContent = () => {
    if (modalLoading) return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-primary rounded-full animate-spin"></div>
        <p className="text-sm text-slate-500">Loading data...</p>
      </div>
    );
    if (modalError) return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-red-500">
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm">{modalError}</p>
      </div>
    );
    if (!modalData || modalData.length === 0) return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p className="text-slate-500 font-medium">No items found</p>
      </div>
    );

    const tableClass = "w-full";
    const theadClass = "bg-slate-50 border-b border-slate-200";
    const tbodyClass = "divide-y divide-slate-100";
    const trClass = "hover:bg-slate-50 transition-colors";

    switch (modalType) {
      case 'available':
        return (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={theadClass}><tr>
                <Th>Tool Name</Th><Th>Serial #</Th><Th>Category</Th><Th>Location</Th><Th>Condition</Th>
              </tr></thead>
              <tbody className={tbodyClass}>
                {modalData.map(tool => (
                  <tr key={tool.tool_id} className={trClass}>
                    <Td><span className="font-semibold text-slate-900">{tool.tool_name}</span></Td>
                    <Td><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{tool.serial_number}</code></Td>
                    <Td>{tool.category || 'N/A'}</Td>
                    <Td>{tool.storage_location || 'Not specified'}</Td>
                    <Td><Badge status={tool.condition || 'good'} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'checked_out':
        return (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={theadClass}><tr>
                <Th>Tool</Th><Th>Employee</Th><Th>Checkout Date</Th><Th>Due Date</Th><Th>Status</Th>
              </tr></thead>
              <tbody className={tbodyClass}>
                {modalData.map(item => (
                  <tr key={item.transaction_id} className={trClass}>
                    <Td>
                      <p className="font-semibold text-slate-900 text-sm">{item.tool_name}</p>
                      <p className="text-xs text-slate-500">{item.serial_number}</p>
                    </Td>
                    <Td>{item.employee_name || `${item.first_name} ${item.last_name}`}</Td>
                    <Td>{new Date(item.checkout_date).toLocaleDateString()}</Td>
                    <Td>{new Date(item.expected_return_date).toLocaleDateString()}</Td>
                    <Td><Badge status={item.checkout_status || item.status || 'active'} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'overdue':
        return (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={theadClass}><tr>
                <Th>Tool</Th><Th>Employee</Th><Th>Due Date</Th><Th>Days Overdue</Th><Th>Status</Th>
              </tr></thead>
              <tbody className={tbodyClass}>
                {modalData.map(item => (
                  <tr key={item.transaction_id} className="bg-red-50 hover:bg-red-100 transition-colors">
                    <Td>
                      <p className="font-semibold text-slate-900 text-sm">{item.tool_name}</p>
                      <p className="text-xs text-slate-500">{item.serial_number}</p>
                    </Td>
                    <Td>{item.employee_name || `${item.first_name} ${item.last_name}`}</Td>
                    <Td>{new Date(item.expected_return_date).toLocaleDateString()}</Td>
                    <Td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        {item.days_overdue} day{item.days_overdue !== 1 ? 's' : ''}
                      </span>
                    </Td>
                    <Td><Badge status="overdue" /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'calibration':
        return (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={theadClass}><tr>
                <Th>Tool</Th><Th>Type</Th><Th>Last Calibration</Th><Th>Next Due</Th><Th>Status</Th>
              </tr></thead>
              <tbody className={tbodyClass}>
                {modalData.map(item => (
                  <tr key={item.calibration_id || item.tool_id} className={trClass}>
                    <Td>
                      <p className="font-semibold text-slate-900 text-sm">{item.tool_name}</p>
                      <p className="text-xs text-slate-500">{item.serial_number}</p>
                    </Td>
                    <Td>{item.calibration_type || 'Standard'}</Td>
                    <Td>{item.last_calibration_date ? new Date(item.last_calibration_date).toLocaleDateString() : 'Never'}</Td>
                    <Td><span className="text-amber-600 font-medium">{new Date(item.next_calibration_date).toLocaleDateString()}</span></Td>
                    <Td><Badge status={item.status} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'low_stock':
        return (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead className={theadClass}><tr>
                <Th>Item Name</Th><Th>Serial #</Th><Th>Available</Th><Th>Minimum</Th><Th>Need to Order</Th><Th>Location</Th>
              </tr></thead>
              <tbody className={tbodyClass}>
                {modalData.map(item => (
                  <tr key={item.tool_id} className="bg-amber-50 hover:bg-amber-100 transition-colors">
                    <Td><span className="font-semibold text-slate-900">{item.tool_name}</span></Td>
                    <Td><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{item.serial_number}</code></Td>
                    <Td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{item.quantity_available}</span>
                    </Td>
                    <Td>{item.quantity_minimum}</Td>
                    <Td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700">+{item.quantity_needed || 0}</span>
                    </Td>
                    <Td>{item.storage_location || 'N/A'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default: return null;
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-primary rounded-full animate-spin"></div>
        <p className="text-sm text-slate-500">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map(card => (
          <button
            key={card.key}
            onClick={() => handleStatCardClick(card.key)}
            className={`bg-white  rounded-xl p-4 text-left cursor-pointer hover:shadow-md transition-all duration-200 shadow-sm border border-slate-200 group`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${card.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <svg className={`w-5 h-10 ${card.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-2xl font-bold leading-tight ${card.valueColor}`}>{summary?.[card.field] || 0}</p>
                <p className="text-xs text-slate-500 font-medium truncate">{card.label}</p>
              </div>
              <svg className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checked Out Tools */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Currently Checked Out</h3>
            <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">{checkedOutTools.length}</span>
          </div>
          {checkedOutTools.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">No tools currently checked out</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  <Th>Tool</Th><Th>Employee</Th><Th>Due Date</Th><Th>Status</Th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {checkedOutTools.slice(0, 5).map(item => (
                    <tr key={item.transaction_id} className="hover:bg-slate-50 transition-colors">
                      <Td>
                        <p className="font-semibold text-slate-900 text-sm">{item.tool_name}</p>
                        <p className="text-xs text-slate-500">{item.serial_number}</p>
                      </Td>
                      <Td>{item.employee_name}</Td>
                      <Td>{new Date(item.expected_return_date).toLocaleDateString()}</Td>
                      <Td><Badge status={item.checkout_status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Calibration Due */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Calibration Due</h3>
            <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">{calibrationDue.length}</span>
          </div>
          {calibrationDue.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-slate-400">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">All tools are up to date</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  <Th>Tool</Th><Th>Type</Th><Th>Due Date</Th><Th>Status</Th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {calibrationDue.slice(0, 5).map(item => (
                    <tr key={item.tool_id} className="hover:bg-slate-50 transition-colors">
                      <Td>
                        <p className="font-semibold text-slate-900 text-sm">{item.tool_name}</p>
                        <p className="text-xs text-slate-500">{item.serial_number}</p>
                      </Td>
                      <Td>{item.calibration_type}</Td>
                      <Td><span className="text-amber-600 font-medium">{new Date(item.next_calibration_date).toLocaleDateString()}</span></Td>
                      <Td><Badge status={item.status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-slate-900">{getModalTitle()}</h2>
              <button onClick={closeModal} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderModalContent()}
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 flex-shrink-0">
              <p className="text-sm text-slate-500">Total: <strong className="text-slate-900">{modalData.length}</strong> item{modalData.length !== 1 ? 's' : ''}</p>
              <button onClick={closeModal} className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
